import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';

import * as PrescriptionActions from './prescription.actions';
import { PrescriptionEntity } from './prescription.models';
import { getMaxValue, getMinValue } from '@care-portals/shared';

export const PRESCRIPTION_FEATURE_KEY = 'prescription';

export interface State extends EntityState<PrescriptionEntity> {
  selectedId?: string | number;
  loading: boolean;
  updating: boolean;
  sending: boolean;
  creating: boolean;
  error?: string | null;
  minId: string;
  maxId: string;
  fulfillmentOrderId: number | null;
}

export interface PrescriptionPartialState {
  readonly [PRESCRIPTION_FEATURE_KEY]: State;
}

export function selectPrescriptionId(prescription: PrescriptionEntity): string {
  //In this case this would be optional since primary key is id
  return prescription._id;
}

export const prescriptionAdapter: EntityAdapter<PrescriptionEntity> =
  createEntityAdapter<PrescriptionEntity>({
    selectId: selectPrescriptionId
  });

export const initialState: State = prescriptionAdapter.getInitialState({
  loading: false,
  updating: false,
  sending: false,
  creating: false,
  minId: null,
  maxId: null,
  fulfillmentOrderId: null
});

const prescriptionReducer = createReducer(
  initialState,
  on(PrescriptionActions.resetRx, state =>
    prescriptionAdapter.removeAll({
      ...state,
      error: null,
      minId: null,
      maxId: null
    })
  ),
  on(PrescriptionActions.loadPrescriptions, state => ({
    ...state,
    loading: true,
    error: null
  })),
  on(
    PrescriptionActions.loadPrescriptionsSuccess,
    (state, { prescriptions, loadType }) => {
      const ids = prescriptions.map(prescription => prescription._id);
      const newMaxId = getMaxValue(ids);
      const newMinId = getMinValue(ids);
      const maxId = newMaxId
        ? state.maxId > newMaxId
          ? state.maxId
          : newMaxId
        : state.maxId;
      const minId = newMinId
        ? state.minId < newMinId
          ? state.minId
          : newMinId
        : state.minId;

      const currentPrescriptions = Object.keys(state.entities).map(
        key => state.entities[key]
      );
      const newPrescriptions =
        loadType === 'newer'
          ? [...prescriptions, ...currentPrescriptions]
          : [...currentPrescriptions, ...prescriptions];

      return prescriptionAdapter.setAll(newPrescriptions, {
        ...state,
        loading: false,
        maxId,
        minId
      });
    }
  ),
  on(PrescriptionActions.loadPrescriptionsFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false
  })),

  on(PrescriptionActions.fetchPrescription, state => ({
    ...state,
    loading: true,
    error: null
  })),
  on(
    PrescriptionActions.fetchPrescriptionSuccess,
    (state, { prescription }) => {
      return prescriptionAdapter.upsertOne(prescription, {
        ...state,
        selectedId: prescription._id,
        loading: false
      });
    }
  ),
  on(PrescriptionActions.fetchPrescriptionFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false
  })),

  on(PrescriptionActions.createPrescription, state => ({
    ...state,
    creating: true,
    error: null
  })),
  on(PrescriptionActions.createPrescriptionSuccess, (state, { prescription }) =>
    prescriptionAdapter.setAll(
      [
        prescription,
        ...Object.keys(state.entities).map(key => state.entities[key])
      ],
      { ...state, creating: false }
    )
  ),
  on(PrescriptionActions.createPrescriptionFailure, (state, { error }) => ({
    ...state,
    creating: false,
    error
  })),

  on(PrescriptionActions.updatePrescription, state => ({
    ...state,
    updating: true,
    error: null
  })),
  on(PrescriptionActions.addFulfillment, state => ({
    ...state,
    updating: true,
    error: null,
    fulfillmentOrderId: null
  })),
  on(PrescriptionActions.addFulfillmentSuccess, (state, { prescription, orderId }) =>
    prescriptionAdapter.updateOne(
      { id: prescription._id, changes: prescription },
      { 
        ...state, 
        updating: false,
        fulfillmentOrderId: orderId
      }
    )
  ),
  on(PrescriptionActions.addFulfillmentFailure, (state, { error }) => ({
    ...state,
    updating: false,
    error,
    fulfillmentOrderId: null
  })),
  on(PrescriptionActions.updatePrescriptionSuccess, (state, { prescription }) =>
    prescriptionAdapter.updateOne(
      { id: prescription._id, changes: prescription },
      { ...state, updating: false }
    )
  ),
  on(PrescriptionActions.updatePrescriptionFailure, (state, { error }) => ({
    ...state,
    updating: false,
    error
  })),

  on(PrescriptionActions.sendRx, state => ({
    ...state,
    sending: true,
    error: null
  })),
  on(PrescriptionActions.sendRxSuccess, (state, { prescription }) =>
    prescriptionAdapter.updateOne(
      { id: prescription._id, changes: prescription },
      { ...state, sending: false }
    )
  ),
  on(PrescriptionActions.sendRxFailure, (state, { error }) => ({
    ...state,
    error,
    sending: false
  })),
  on(PrescriptionActions.resetFulfillmentState, state => ({
    ...state,
    fulfillmentOrderId: null
  })),
  on(
    PrescriptionActions.sendPrescriptionPdfToChatSuccess,
    (state, { rxId }) =>
      prescriptionAdapter.updateOne(
        { id: rxId, changes: { transferredOutAt: new Date() } },
        state
      )
  )
);

export function reducer(state: State | undefined, action: Action) {
  return prescriptionReducer(state, action);
}
